/**
 * E-Mail-Konfiguration und Eingangsprotokoll (Spec 3.2 / 6).
 *
 * Single Source of Truth fuer API, Admin-UI und Worker.
 *
 * **Seit dem Verbindungs-Vault (ADR 0017) stehen hier keine Credentials mehr.**
 * `queues.inbound_config` und `queues.outbound_config` beschreiben nur noch,
 * *wie* eine Queue den Kanal benutzt — welche Adressen sie empfaengt, aus
 * welchem Ordner sie liest, unter welchem Absender sie schreibt. *Womit* sie
 * das tut, steht in einer Verbindung (`schemas/connection.ts`), auf die
 * `queues.inbound_connection_id` / `queues.outbound_connection_id` zeigen.
 *
 * Damit gibt es hier auch keine drei Schema-Ebenen mehr: gespeichert, Eingabe
 * und Ausgabe sind identisch, weil nichts zu redigieren ist. Die Trennung
 * „gespeichert / Eingabe / oeffentlich" lebt jetzt in `connection.ts`.
 */
import { z } from 'zod';
import { emailSchema, uuidSchema } from './common.js';
import { paginationQuerySchema } from '../envelope.js';
// ────────────────────────────────────────────────────────── Empfang
export const EMAIL_INBOUND_CHANNELS = ['smtp', 'imap'];
export const emailInboundChannelSchema = z.enum(EMAIL_INBOUND_CHANNELS);
/** Untergrenze aus Spec 6.1.2 („alle 60–120 s"); Obergrenze = 1 h. */
export const IMAP_POLL_INTERVAL_MIN_SECONDS = 60;
export const IMAP_POLL_INTERVAL_MAX_SECONDS = 3600;
export const IMAP_POLL_INTERVAL_DEFAULT_SECONDS = 120;
/** Implizites TLS. Steht hier, weil `imap_account`-Verbindungen ihn teilen. */
export const IMAP_PORT_DEFAULT = 993;
const smtpInboundBase = z.object({
    channel: z.literal('smtp'),
    /**
     * Empfangsadressen dieser Queue (Spec 3.2). Beim `RCPT TO` wird die
     * Adresse hiergegen aufgeloest; Plus-Addressing zaehlt zur Basisadresse.
     */
    addresses: z.array(emailSchema).min(1).max(20),
});
/**
 * Die IMAP-**Benutzung** durch diese Queue — Host, Benutzer und Passwort
 * stehen in der `imap_account`-Verbindung (ADR 0017), auf die
 * `queues.inbound_connection_id` zeigt.
 *
 * Ordner und Poll-Intervall bleiben hier: zwei Queues duerfen dasselbe Konto
 * mit verschiedenen Ordnern und Takten benutzen.
 */
const imapInboundBase = z.object({
    channel: z.literal('imap'),
    folder: z.string().min(1).max(200).default('INBOX'),
    /** Zielordner nach der Verarbeitung. `null` = nur als gelesen markieren. */
    processed_folder: z.string().min(1).max(200).nullable().default(null),
    poll_interval_seconds: z
        .number()
        .int()
        .min(IMAP_POLL_INTERVAL_MIN_SECONDS)
        .max(IMAP_POLL_INTERVAL_MAX_SECONDS)
        .default(IMAP_POLL_INTERVAL_DEFAULT_SECONDS),
});
/**
 * Normalform einer Empfangsadresse — der Schluessel, unter dem eine
 * SMTP-Adresse einer Queue zugeordnet ist (`queue_inbound_addresses.address`).
 *
 * `Local+Tag@Domain` → `local@domain`, kleingeschrieben, ohne spitze Klammern.
 * Der lokale Teil ist laut RFC 5321 eigentlich case-sensitiv; in der Praxis
 * behandelt ihn jeder Mailserver case-insensitiv. Plus-Addressing gehoert zur
 * Basisadresse (Spec 6.2, Stufe 2) — sonst muesste jede Antwortadresse einzeln
 * konfiguriert werden.
 *
 * Hier in `shared`, weil zwei Seiten dieselbe Normalform brauchen: der
 * SMTP-Empfaenger beim Aufloesen (`mail/routing.ts`) und die
 * Queue-Konfiguration beim Speichern (`services/queues.ts`).
 */
export function normalizeInboundAddress(address) {
    const trimmed = address.trim().replace(/^<|>$/g, '').toLowerCase();
    const at = trimmed.lastIndexOf('@');
    if (at <= 0)
        return trimmed;
    const local = trimmed.slice(0, at);
    const domain = trimmed.slice(at + 1);
    const plus = local.indexOf('+');
    return `${plus >= 0 ? local.slice(0, plus) : local}@${domain}`;
}
/**
 * Empfangs-Konfiguration einer Queue. Gespeichert, Eingabe und Ausgabe sind
 * dasselbe Schema — hier steht kein Geheimnis mehr (ADR 0017).
 */
export const inboundConfigSchema = z.discriminatedUnion('channel', [
    smtpInboundBase,
    imapInboundBase,
]);
/**
 * Bestandszeilen aus der Zeit vor ADR 0017 tragen in `inbound_config` noch
 * `host`/`user`/`password_encrypted`. Migration `0021` raeumt sie ab; dieser
 * Durchlauf ist die zweite Linie und wirft unbekannte Felder weg.
 */
export function normalizeInboundConfig(value) {
    if (value === null || value === undefined)
        return null;
    const parsed = inboundConfigSchema.safeParse(value);
    return parsed.success ? parsed.data : null;
}
// ────────────────────────────────────────────────────────── Versand
/**
 * `mock` ist der Dev-/Test-Adapter: er verschickt nichts, sondern legt die
 * Mail in einem Postausgang im Speicher ab. Produktiv taugt nur
 * `mailersend`/`zeptomail`/`scaleway` (Spec 6.4 + ADR 0013).
 */
export const OUTBOUND_MAIL_PROVIDERS = ['mailersend', 'zeptomail', 'scaleway', 'mock'];
export const outboundMailProviderSchema = z.enum(OUTBOUND_MAIL_PROVIDERS);
/**
 * Region der Scaleway-TEM-API — sie steht **im Pfad**
 * (`…/regions/<region>/emails`), nicht in einem Header.
 *
 * Scaleway bietet Transactional Email derzeit **nur in Paris** (`fr-par`) an;
 * das ist deshalb der Vorgabewert. Die beiden anderen sind die uebrigen
 * Scaleway-Regionen und stehen hier, damit eine spaetere Freischaltung keine
 * Migration braucht. Wer eine Region waehlt, in der TEM (noch) nicht laeuft,
 * bekommt vom Provider ein `404` — sichtbar als dauerhafter Fehler an der
 * Nachricht, nicht als stiller Verlust.
 */
export const SCALEWAY_REGIONS = ['fr-par', 'nl-ams', 'pl-waw'];
export const scalewayRegionSchema = z.enum(SCALEWAY_REGIONS);
export const SCALEWAY_REGION_DEFAULT = 'fr-par';
/** Absenderregel je Queue (Spec 6.4). */
export const SEND_AS_MODES = ['queue', 'agent'];
export const sendAsSchema = z.enum(SEND_AS_MODES);
/** Reply-Domain: reiner Hostname, keine URL, kein `@`. */
export const hostnameSchema = z
    .string()
    .min(3)
    .max(253)
    .regex(/^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/i, 'Bitte eine Domain angeben, z. B. support.example.com.')
    .toLowerCase();
/**
 * Der **Absender** einer Queue (Spec 6.4) — wer im `From:` steht, unter
 * welchem Namen, und ueber welche Domain Antworten zurueckkommen.
 *
 * Seit ADR 0017 steht hier **kein Provider und kein Schluessel** mehr: das
 * „womit" liegt in einer `mail_provider`-Verbindung, auf die
 * `queues.outbound_connection_id` zeigt (oder, wenn die Queue keine eigene
 * Wahl trifft, `tenants.default_mail_connection_id`).
 *
 * Gespeichert, Eingabe und Ausgabe sind deshalb dasselbe Schema.
 */
export const outboundConfigSchema = z.object({
    from_address: emailSchema,
    from_name: z.string().min(1).max(200),
    send_as: sendAsSchema.default('queue'),
    /** Domain der Plus-Adresse fuer Antworten (Spec 6.2, Stufe 2). */
    reply_domain: hostnameSchema,
});
/**
 * Bestandszeilen aus der Zeit vor ADR 0017 tragen in `outbound_config` noch
 * `provider` und `api_key_encrypted`. Migration `0021` raeumt sie ab; dieser
 * Durchlauf ist die zweite Linie und wirft unbekannte Felder weg.
 */
export function normalizeOutboundConfig(value) {
    if (value === null || value === undefined)
        return null;
    const parsed = outboundConfigSchema.safeParse(value);
    return parsed.success ? parsed.data : null;
}
/**
 * Welches Geheimnis dieser Provider braucht — die eine Stelle, die das
 * entscheidet. `null` = keins (der Mock-Adapter verschickt nichts).
 */
export function secretFieldOf(provider) {
    if (provider === 'mock')
        return null;
    return provider === 'scaleway' ? 'secret_key' : 'api_key';
}
// ───────────────────────────────────────────── Plus-Addressing / Token
/**
 * Ticket-Token fuer die Antwortadresse (Spec 6.2, Stufe 2). Crockford-Base32
 * ohne `I`, `L`, `O`, `U` — damit ist der Token in einer E-Mail-Adresse
 * unzweideutig vorlesbar und kollidiert nicht mit Wortfiltern.
 */
export const TICKET_TOKEN_ALPHABET = '0123456789abcdefghjkmnpqrstvwxyz';
export const TICKET_TOKEN_LENGTH = 16;
export const ticketTokenSchema = z
    .string()
    .length(TICKET_TOKEN_LENGTH)
    .regex(new RegExp(`^[${TICKET_TOKEN_ALPHABET}]+$`));
/** `support` + `+t_<token>` + `@domain` (Spec 6.2). */
export const TICKET_TOKEN_PREFIX = 't_';
export function replyAddressFor(fromAddress, replyDomain, token) {
    const localPart = fromAddress.split('@')[0] ?? 'support';
    return `${localPart}+${TICKET_TOKEN_PREFIX}${token}@${replyDomain}`;
}
/** Zieht den Ticket-Token aus einer Plus-Adresse. `null` = keine Treffer. */
export function ticketTokenFromAddress(address) {
    const match = /\+t_([0-9abcdefghjkmnpqrstvwxyz]+)@/i.exec(address);
    const token = match?.[1]?.toLowerCase();
    return token !== undefined && token.length === TICKET_TOKEN_LENGTH ? token : null;
}
/** Betreff-Token `[#T-2026-0042]` (Spec 6.2, Stufe 3). */
export function subjectWithTicketNumber(subject, number) {
    return subject.includes(`[#${number}]`) ? subject : `${subject} [#${number}]`;
}
export function ticketNumberFromSubject(subject) {
    return /\[#(T-\d{4}-\d{4,})\]/i.exec(subject)?.[1]?.toUpperCase() ?? null;
}
// ───────────────────────────────────────────────── Eingangsprotokoll
/**
 * Urteil je eingehender Mail (Spec 3.2 / 6.3).
 *
 * `spam` deckt beide rspamd-Faelle ab (Spec 6.1.1): die in der SMTP-Session
 * mit `554` abgewiesene Mail **und** die stillschweigend einbehaltene. Welcher
 * es war, steht im `reason` (`rspamd-reject` bzw. `rspamd-add-header`).
 *
 * `rejected` traegt das Absender-Rate-Limit aus Spec 6.3
 * (`reason = 'rate-limit'`, siehe ADR 0016) — kein eigener Enum-Wert, weil
 * „angenommen, aber kein Ticket" fachlich genau das ist.
 */
export const EMAIL_VERDICTS = [
    'accepted',
    'duplicate',
    'loop',
    'bounced',
    'rejected',
    'spam',
];
export const emailVerdictSchema = z.enum(EMAIL_VERDICTS);
export const emailInboundLogSchema = z.object({
    id: uuidSchema,
    tenant_id: uuidSchema,
    queue_id: uuidSchema.nullable(),
    ticket_id: uuidSchema.nullable(),
    channel: emailInboundChannelSchema,
    message_id: z.string().nullable(),
    from_address: z.string().nullable(),
    subject: z.string().nullable(),
    verdict: emailVerdictSchema,
    /** rspamd-Score (Spec 6.1.1); `null`, wenn rspamd nicht befragt wurde. */
    spam_score: z.number().nullable(),
    /** Pfad der Rohmail relativ zu `DATA_DIR`; `null` nach der Retention. */
    raw_ref: z.string().nullable(),
    /** Kurzbegruendung des Urteils, englisch (z. B. `auto-submitted`). */
    reason: z.string().nullable(),
    created_at: z.iso.datetime(),
});
export const emailInboundLogQuerySchema = paginationQuerySchema.extend({
    verdict: emailVerdictSchema.optional(),
    queue_id: uuidSchema.optional(),
});
// ───────────────────────────────────── Quarantaene + Bayes-Lernen (6.1.1)
/**
 * „Trotzdem verarbeiten": die persistierte Rohmail eines einbehaltenen
 * Eintrags noch einmal durch die normale Pipeline schicken. Das Ergebnis ist
 * dasselbe Urteil, das der Eingang auch geliefert haette.
 */
export const emailReprocessResultSchema = z.object({
    verdict: emailVerdictSchema,
    ticket_id: uuidSchema.nullable(),
    reason: z.string().nullable(),
});
/** Richtung des Bayes-Trainings (Spec 6.1.1: Buttons „Spam" / „Kein Spam"). */
export const EMAIL_LEARN_KINDS = ['spam', 'ham'];
export const emailLearnKindSchema = z.enum(EMAIL_LEARN_KINDS);
export const emailLearnInputSchema = z.object({ kind: emailLearnKindSchema });
export const emailLearnResultSchema = z.object({
    kind: emailLearnKindSchema,
    /** `false` = rspamd kannte die Mail schon (HTTP 208), kein Fehler. */
    learned: z.boolean(),
});
// ─────────────────────────────────────────────────── Fehlgeschlagene Jobs
/** Anzeige der BullMQ-Failed-Queue im Admin-UI (CLAUDE.md, Override „DLQ"). */
export const failedJobSchema = z.object({
    id: z.string(),
    queue: z.string(),
    name: z.string(),
    attempts: z.number().int().min(0),
    failed_reason: z.string(),
    failed_at: z.iso.datetime().nullable(),
});
//# sourceMappingURL=email.js.map